import React, { useEffect, useMemo } from "react";
import * as Sentry from "@sentry/react";
import { Loader, theme } from "@findemdev/maui";
import { ReactNode } from "react";
import { ThemeProvider } from "styled-components";
import AppStyles from "./App.styled";
import UserContext from "./contexts/UserContext";
import { IUserPreference, UserModel } from "./models/user";
import { FullStory, init as initFullStory } from '@fullstory/browser';
import { loadScript } from "@findemdev/findem-helpers";
import { getAppName } from "./utils/common";
import { APP_NAMES } from "./types/app";
import { useGetCurrentUserQuery, useGetUserPreferencesQuery } from "./services/matches";

interface IAppProviders {
  children: ReactNode
}

const AppProviders = ({ children }: IAppProviders) => {
  const [userModel, setUserModel] = React.useState<UserModel | null>();

  // hooks
  const { data: currentUser, isLoading } = useGetCurrentUserQuery();
  const { data: userPrefs, isLoading: isPrefsLoading } = useGetUserPreferencesQuery(
    { joid: currentUser?.uid || '' },
    { skip: !currentUser?.uid }
  );

  const isUserLoading = isLoading || isPrefsLoading;
  const isUserModelLoading = useMemo(() => !!currentUser?.uid && !userModel, [currentUser, userModel]);

 useEffect(() => {
  if(!currentUser?.uid && !isLoading && window.location.pathname !== '/login') {
    window.location.href = '/login';
  }

  if(currentUser?.uid && window.location.pathname === '/') {
    window.location.href = '/my-apps';
  }
 }, [currentUser, isLoading])

  React.useEffect(() => {
    if (currentUser?.uid && !isPrefsLoading) {
      const _user = new UserModel({ ...currentUser, appName: getAppName() });
      _user.setUserPreference(userPrefs as IUserPreference ?? {});
      setUserModel(_user);
    }
  }, [currentUser, isPrefsLoading, userPrefs]);

  React.useEffect(() => {
    (window as any).AppcuesSettings = {
      enableURLDetection: true
    };
    if (currentUser?.uid) {
      loadScript('//fast.appcues.com/127109.js', () => {
        if ((window as any).Appcues) {
          (window as any).Appcues.identify(currentUser.uid, {
            name: currentUser.name,
            email: currentUser.email,
            uid: currentUser.uid,
            company: currentUser.company,
            created_at: currentUser.account_creation,
            roles: currentUser.roles.join(', '),
            init_at: Date.now(),
            Persona: undefined,
            Search_Cert: undefined,
          });
        }
      });
      FullStory('setProperties', {
        type: 'user',
        properties: {
          name: currentUser.name,
          email: currentUser.email,
          company: currentUser.company,
          created_at: currentUser.account_creation,
          roles: currentUser.roles.join(', '),
          init_at: Date.now(),
          Persona: undefined,
          Search_Cert: undefined,
        },
      });
      Sentry.setUser({
        id: currentUser.uid,
        email: currentUser.email,
        username: currentUser.name,
      });
    }
  }, [currentUser]);

  initFullStory({
    orgId: getAppName() === APP_NAMES.RRA ? "o-22GNK5-na1" : "VB5PS",
    devMode: process.env?.NODE_ENV === "development",
  });
  if (
    isUserLoading ||
    isUserModelLoading ||
    (!currentUser?.uid && window.location.pathname !== "/login")
  ) {
    return <Loader $fullPage={true} />;
  } else {
    return (
      <UserContext.Provider value={userModel as UserModel}>
        <ThemeProvider theme={theme}>
          <AppStyles />
          {children}
        </ThemeProvider>
      </UserContext.Provider>
    );
  }
}

export default AppProviders;
